import React, { useCallback, useState, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { currentProfileQuery } from "../../features/users/api/profileQueries";
import {
  Alert,
  AlertTitle,
  Box,
  Drawer,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { JSONSchema7 } from "json-schema";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { EforceIncidentModel } from "../../shared/interfaces/EforceModels";

import {
  useFormDataStore,
  useFormDataTemporal,
  useCanUndo,
  useCanRedo,
} from "./store/formDataStore";
import { useToast } from "../../shared/toast";
import { useInactivityTimer } from "../../shared/hooks/useInactivityTimer";
import { useLayout, usePageHeader } from "../../context/LayoutContext";

import { EditExtractedInformation } from "./EditExtractedInformation";
import { NarrativeOnlyEditor } from "./components/NarrativeOnlyEditor";
import {
  EditSectionProvider,
  type EditSectionContextValue,
} from "./EditSectionContext";
import { EditExtractedInformationSkeleton } from "../../components/skeletons/EditExtractedInformationSkeleton";
import { WarningDisplay } from "../../components/feedback/WarningDisplay";
import { InactivityModal } from "@/shared/ui/modals/InactivityModal";

import { useReportData } from "./hooks/useReportData";
import { useReportWebSocket } from "./hooks/useReportWebSocket";
import { useReportSaving, SaveCancelledError } from "./hooks/useReportSaving";
import { useGenerateFullReport } from "./hooks/useGenerateFullReport";
import { EditSectionFooter } from "./components/EditSectionFooter";
import { ChatPanelContainer } from "../../components/chat/ChatPanelContainer";
import { ChatPanel } from "../../components/chat/ChatPanel";

import { eforceSchemaMapping } from "@/features/reports/config/eforceSchemaMapping";
import { genericSchemaMapping } from "@/features/reports/config/genericSchemaMapping";
import { CoReviewBanner } from "../../components/coReview/CoReviewBanner";
import { ReviewHistoryDialog } from "../../components/coReview/ReviewHistoryDialog";
import { ReportStatus } from "../../shared/interfaces/ReportStatus";
import { useCoApprovalEnabled } from "@/features/co-review/hooks/useCoApprovalEnabled";
import { SectionCommentRail } from "../../components/coReview/SectionCommentRail";
import { NarrativeWithInlineComments } from "../../components/coReview/NarrativeWithInlineComments";
import { coApprove, coRetract } from "@/features/co-review/api/coReviewApi";
import {
  useReplyToCoComment,
  useResolveCoComment,
  useUnresolveCoComment,
} from "@/features/co-review/api/coReviewMutations";
import { SignatureDialog } from "../../shared/ui";
import { RetractConfirmationDialog } from "../../components/coReview/RetractConfirmationDialog";
import Button from "@mui/material/Button";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import UndoOutlinedIcon from "@mui/icons-material/UndoOutlined";
import { useUserDirectory } from "@/features/users/api/userDirectory";
import { useAuth } from "@workos-inc/authkit-react";
import { useOrgId } from "../../shared/auth/orgContext";
import { Paper, Typography, Stack } from "@mui/material";

import { prettifySectionPath } from "@/features/reports/utils/prettifySectionPath";
import {
  extractNarrativeTexts,
  splitCoComments,
} from "@/features/co-review/model/coReviewHelpers";

const EditSectionPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const { data: profileData } = useQuery(currentProfileQuery());
  const userProfile = profileData ?? null;

  // Use global layout context for chat visibility
  const { sidePanelOpen, setSidePanelOpen, setChatAvailable } = useLayout();

  // Ref to expose chat's sendMessage function to narrative widgets
  const chatSendMessageRef = useRef<
    ((message: string) => Promise<void>) | null
  >(null);

  // Ref to capture form data snapshot before agent changes (for change detection)
  const formDataSnapshotRef = useRef<EforceIncidentModel | null>(null);

  // Set page header in app bar
  usePageHeader({
    title: "Generated Report Editing",
    subtitle:
      "Use the Save button to save your changes. Sign off on every section to reach final review.",
    icon: <AutoAwesomeIcon />,
  });

  const toast = useToast();

  const formData = useFormDataStore((state) => state.formData);
  const setFormData = useFormDataStore((state) => state.setFormData);
  const setCurrentSection = useFormDataStore(
    (state) => state.setCurrentSection,
  );
  const { undo, redo } = useFormDataTemporal();
  const canUndo = useCanUndo();
  const canRedo = useCanRedo();

  // Custom data hooks
  const {
    reportID,
    videoData,
    setVideoData,
    videoLoadStatus,
    showRMSCommit,
    setShowRMSCommit,
    loading,
    jsonSchema,
    reportType,
    setReportType,
    lastModifiedTime,
    setLastModifiedTime,
    enableEditing,
    reFetchExtractedIncident,
    reportRms,
    coComments,
    coReturnedAt,
    reportStatus,
    reportOwnerUserId,
    narrativeOnly,
  } = useReportData();
  const coApprovalEnabled = useCoApprovalEnabled();

  const { user } = useAuth();
  const organizationId = useOrgId();
  // Warm the user-name cache so comment author labels can resolve display names.
  useUserDirectory(organizationId ?? undefined);
  // Only the report's owning officer can finish review / confirm sections.
  // Other viewers (COs reviewing a submission) see no submit surface.
  // Fail closed: ownership is only granted on a positive match against a
  // known owner id. While the owner is still loading, isReportOwner is false
  // so owner-only controls stay hidden (brief flicker for the real owner,
  // but no leakage of owner UI to a CO viewer).
  const isOwnerKnown = reportOwnerUserId != null;
  const isReportOwner =
    !!user?.id && isOwnerKnown && reportOwnerUserId === user.id;
  // Officers reach EditSectionPage in CHANGES_REQUESTED to address CO feedback;
  // that's exactly when they need to mark comments resolved. The backend allows
  // PUT /comment/{id} in either IN_CO_REVIEW or CHANGES_REQUESTED. Gate on
  // isReportOwner so a CO viewer can't resolve the officer's comments — that's
  // an owner-only action; the CO's finalize path goes through canCoFinalize.
  const canResolveComments =
    reportStatus === ReportStatus.CHANGES_REQUESTED && isReportOwner;
  const officerUser = React.useMemo(
    () => ({
      userId: user?.id ?? "officer-demo-user",
      displayName:
        [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
        user?.email ||
        "Officer",
    }),
    [user],
  );

  // Comment actions go through the shared co-review mutations: the cache is
  // patched optimistically (both this page's "current" list and CoReviewPage's
  // "all" list), rolled back + toasted on failure. Handlers stay async so
  // callers that await still resolve after the server settles; rejections are
  // swallowed because the mutation already toasted.
  const resolveCommentMutation = useResolveCoComment(reportID ?? "");
  const unresolveCommentMutation = useUnresolveCoComment(reportID ?? "");
  const replyCommentMutation = useReplyToCoComment(reportID ?? "");

  const handleResolveComment = async (commentId: string) => {
    if (!reportID || !isReportOwner) return;
    await resolveCommentMutation
      .mutateAsync({ commentId, resolvedByUserId: officerUser.userId })
      .catch(() => undefined);
  };
  const handleUnresolveComment = async (commentId: string) => {
    if (!reportID || !isReportOwner) return;
    await unresolveCommentMutation
      .mutateAsync({ commentId, resolvedByUserId: null })
      .catch(() => undefined);
  };

  const handleReplyComment = async (parentCommentId: string, text: string) => {
    if (!reportID) return;
    const parent = coComments.find((c) => c.commentId === parentCommentId);
    if (!parent) {
      toast.error("Cannot reply: parent comment is no longer visible.");
      return;
    }
    await replyCommentMutation
      .mutateAsync({ parent, text, author: officerUser })
      .catch(() => undefined);
  };

  // TODO: re-enable "Fix with AI" once the real backend integration lands.
  // The handler below is a mock — `setTimeout(1200ms)` then auto-resolves the
  // comment. Commented out so the sparkle button is hidden in the v1 release.
  // Restore by uncommenting this block AND the three prop passes
  // (`onFixWithAi` / `onFixWithAiComment`) further down in this file.
  // const handleFixCommentWithAi = async (commentId: string) => {
  //   if (!reportID || !isReportOwner) return;
  //   await new Promise((r) => setTimeout(r, 1200));
  //   // The mutation patches the comments cache optimistically and toasts
  //   // its own failure; only the success toast is bespoke here.
  //   await resolveCommentMutation
  //     .mutateAsync({ commentId, resolvedByUserId: officerUser.userId })
  //     .then(() =>
  //       toast.success("AI applied the suggestion. Comment marked resolved."),
  //     )
  //     .catch(() => undefined);
  // };

  const {
    narrativeComments,
    generalComments,
    commentsBySection,
    narrativeCommentsByIndex,
  } = React.useMemo(() => splitCoComments(coComments), [coComments]);

  const narrativeTexts = React.useMemo(
    () => extractNarrativeTexts(formData as Record<string, unknown> | null),
    [formData],
  );

  // Officer-side prior-cycle history (spec §7) is shown on the CO side; the officer focuses
  // on the current cycle's comments while editing. Skip the history accordion here.

  // Wrapped fetch that captures snapshot before fetching (for agent change detection)
  const fetchReportDataWithSnapshot = useCallback(async () => {
    // Capture current form data BEFORE the fetch
    const currentFormData = useFormDataStore.getState().formData;
    formDataSnapshotRef.current = currentFormData
      ? JSON.parse(JSON.stringify(currentFormData))
      : null;

    console.log("[Agent Refresh] Captured snapshot before fetch:", {
      hasSnapshot: !!formDataSnapshotRef.current,
    });

    // Call the actual fetch
    await reFetchExtractedIncident();
  }, [reFetchExtractedIncident]);

  // Inactivity Timer State (managed here to coordinate with WS)
  const [inactivityExpiryTimestamp, setInactivityExpiryTimestamp] =
    useState<number>((Date.now() + 60000 * 5) / 1000);

  // Warnings State
  const [warnings, setWarnings] = useState<string[]>([]);

  const [historyOpen, setHistoryOpen] = useState(false);
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [retractConfirmOpen, setRetractConfirmOpen] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isRetracting, setIsRetracting] = useState(false);

  // CO-only actions on a CHANGES_REQUESTED report: approve outright, or
  // retract the rejection so the report is back IN CO REVIEW. Officers
  // (report owners) never see these — they're editing to address feedback.
  // Require isOwnerKnown so the CO-finalize controls don't briefly appear to
  // the actual owner during the pending window where isReportOwner is still
  // false because reportOwnerUserId hasn't loaded yet.
  const canCoFinalize =
    coApprovalEnabled &&
    isOwnerKnown &&
    !isReportOwner &&
    reportStatus === ReportStatus.CHANGES_REQUESTED;

  const handleApprove = async () => {
    if (!reportID) return;
    setIsApproving(true);
    try {
      await coApprove({ reportId: reportID });
      toast.success("Report approved. Ready to submit to Records System.");
      setSignatureDialogOpen(false);
      navigate(`/co-review/${reportID}`);
    } catch (err) {
      const e = err as { message?: string };
      toast.error(e?.message || "Failed to approve report.");
    } finally {
      setIsApproving(false);
    }
  };

  const handleRetract = async () => {
    if (!reportID) return;
    setIsRetracting(true);
    try {
      await coRetract({ reportId: reportID });
      toast.success("Rejection retracted. Report is back in Commanding Officer review.");
      setRetractConfirmOpen(false);
      navigate(`/co-review/${reportID}`);
    } catch (err) {
      const e = err as { message?: string };
      toast.error(e?.message || "Failed to retract rejection.");
    } finally {
      setIsRetracting(false);
    }
  };

  // Inactivity Timer Hook
  const handleSessionTimeout = useCallback(() => {
    // Server drives timeout (close code 3008)
  }, []);

  const {
    showWarning: showTimeoutWarning,
    setWarningState,
    clearInactivityTimer,
  } = useInactivityTimer({
    countdownDuration: 1 * 60 * 1000,
    onTimeout: handleSessionTimeout,
  });

  // Saving Logic Hook
  const liveWebSocket = React.useRef<WebSocket | null>(null);

  const {
    isSaving,
    setIsSaving,
    saveDeferredRef,
    handleSaveAndExit,
    handleTimeoutSave,
    handleInstantSave,
    handleSaveCurrentChanges,
  } = useReportSaving({ liveWebSocket, showError: toast.error });

  // Full-report generation (narrative-only mode only). Kicks off the jobs run
  // and polls to completion, then refetches so the editor flips to full mode.
  // Declared before the WebSocket hook so the editing socket can be torn down
  // while a run is in flight.
  // Flatten the single/multi video shape so the tracked full-report job
  // carries the same evidence ids and titles the generation flow records.
  const videoList = Array.isArray(videoData)
    ? videoData
    : videoData
      ? [videoData]
      : [];
  const {
    isGeneratingFull,
    startGeneration,
    generationStartedAt,
    generationTimeoutMs,
  } = useGenerateFullReport({
    reportId: reportID ?? null,
    evidenceIDs: videoList.map((v) => v.id),
    videoTitles: videoList.map((v) => v.title),
    narrativeOnly,
    onComplete: reFetchExtractedIncident,
    showError: toast.error,
    showSuccess: toast.success,
  });

  // WebSocket Hook
  const { handleUserAction, continueSession, isSessionReady, sessionSocket } = useReportWebSocket({
    reportID,
    enableEditing,
    showError: toast.error,
    showSuccess: toast.success,
    showTimeoutWarning,
    setWarningState,
    setInactivityExpiryTimestamp,
    refetchIncident: reFetchExtractedIncident,
    setIsSaving,
    saveDeferredRef,
    setLastModifiedTime,
    setShowRMSCommit,
    setReportType,
    setVideoData,
    setWarnings,
    liveWebSocket,
    // While the full report regenerates the backend takes the report lock;
    // tear down the editing socket and suppress its disconnect-redirects so
    // the officer stays on the page watching the progress timer.
    isGeneratingFull,
  });

  // Set chat availability on mount and control visibility based on editing
  // permission. The AI Assistant is also hidden when the report is in
  // CHANGES_REQUESTED so the v1 CO review release ships without AI behavior
  // on the rejected-report flow (see also the ChatPanelContainer gate below).
  const aiAssistantEnabled =
    isSessionReady && reportStatus !== ReportStatus.CHANGES_REQUESTED;
  React.useEffect(() => {
    setChatAvailable(aiAssistantEnabled);
    // Desktop keeps the auto-opened docked panel; below lg the chat is a
    // toggleable drawer that starts closed (the floating button opens it).
    setSidePanelOpen(aiAssistantEnabled && isLgUp);
    return () => {
      setChatAvailable(false);
      setSidePanelOpen(false);
    };
  }, [setChatAvailable, setSidePanelOpen, aiAssistantEnabled, isLgUp]);

  // Persist the latest narrative before kicking off generation. Narrative edits
  // are local store writes (updateFormData(_, false)); requestFullReport sends
  // only the reportId, so the agent reads the backend's persisted narrative.
  // Without a save+ack the run would expand STALE narrative text. Mirrors the
  // submit guard: require an OPEN socket and the server's save ack, aborting on
  // failure/timeout so generation never starts against unsaved changes.
  const handleGenerateFullReport = useCallback(async () => {
    if (
      !liveWebSocket.current ||
      liveWebSocket.current.readyState !== WebSocket.OPEN
    ) {
      toast.error(
        "Connection to server lost. Please refresh the page to make edits.",
      );
      return;
    }
    await handleSaveCurrentChanges();
    try {
      const pendingSave = saveDeferredRef.current;
      if (!pendingSave) return;
      await pendingSave.promise;
    } catch (error) {
      // The save was cancelled because the editing socket was torn down
      // (navigation away / reconnect). The user is already leaving — abort
      // silently rather than show a misleading save-failure toast.
      if (error instanceof SaveCancelledError) return;
      console.error("Save was not acknowledged before generation:", error);
      toast.error(
        "Your latest changes could not be saved. Please try generating again.",
      );
      return;
    }
    await startGeneration();
  }, [
    liveWebSocket,
    handleSaveCurrentChanges,
    saveDeferredRef,
    startGeneration,
    toast,
  ]);

  const schemaMappingMap = {
    EF: eforceSchemaMapping,
    KAR: genericSchemaMapping,
  };

  const getSchemaObjectProperties = (schema: JSONSchema7): string[] => {
    if (!schema.properties) return [];
    const properties: string[] = [];
    for (const key in schema.properties) {
      const curProp = schema.properties[key] as JSONSchema7;
      if (curProp.type?.includes("array") || curProp.type?.includes("object")) {
        properties.push(key);
      }
    }
    return properties;
  };

  const handleUndo = () => {
    if (!isSessionReady || !liveWebSocket.current) return;
    undo();
    handleUserAction("Undo change");

    if (!jsonSchema) return;

    const { currentSection } = useFormDataStore.getState();
    const sections = getSchemaObjectProperties(jsonSchema);
    const totalSections = 1 + sections.length;

    if (currentSection >= totalSections) {
      setCurrentSection(totalSections - 1);
    }
  };

  const handleRedo = () => {
    if (!isSessionReady || !liveWebSocket.current) return;
    redo();
    handleUserAction("Redo change");

    if (!jsonSchema) return;

    const { currentSection } = useFormDataStore.getState();
    const sections = getSchemaObjectProperties(jsonSchema);
    const totalSections = 1 + sections.length;

    if (currentSection >= totalSections) {
      setCurrentSection(totalSections - 1);
    }
  };

  const handleUpdate = async (
    updatedModel: EforceIncidentModel,
    save: boolean,
  ) => {
    if (!isSessionReady || !liveWebSocket.current) return;
    const newModel = JSON.parse(JSON.stringify(updatedModel));
    setFormData(newModel);
    if (save) {
      await handleSaveCurrentChanges();
    }
  };

  const handleSubmit = async () => {
    if (!formData) {
      toast.error("No report data loaded to submit.");
      return;
    }

    // save before navigating — handleSaveCurrentChanges resolves at SEND time;
    // the server's ack resolves the deferred. The signing page must never
    // render (and an officer must never sign) ahead of the server confirming
    // the save, so await the ack and abort the handoff on failure/timeout.
    if (
      !liveWebSocket.current ||
      liveWebSocket.current.readyState !== WebSocket.OPEN
    ) {
      // handleSaveChanges would early-return with its own toast; without an
      // OPEN socket there is no deferred to await — do not navigate.
      toast.error(
        "Connection to server lost. Please refresh the page to make edits.",
      );
      return;
    }
    await handleSaveCurrentChanges();
    try {
      const pendingSave = saveDeferredRef.current;
      if (!pendingSave) return;
      await pendingSave.promise;
    } catch (error) {
      // The save was cancelled because the editing socket was torn down
      // (navigation away / reconnect). The user is already leaving — abort
      // silently rather than show a misleading save-failure toast.
      if (error instanceof SaveCancelledError) return;
      console.error("Save was not acknowledged before submit:", error);
      toast.error(
        "Your latest changes could not be saved. Please try submitting again.",
      );
      return;
    }
    navigate(`/final-edits/${reportID}`, {
      state: {
        modelData: formData,
        showRMSCommit: showRMSCommit,
        jsonSchema: jsonSchema,
        schemaMapping:
          schemaMappingMap[reportRms as keyof typeof schemaMappingMap],
      },
    });
  };

  const handleContinueSession = useCallback(() => {
    clearInactivityTimer();
    continueSession();
  }, [clearInactivityTimer, continueSession]);

  // One chat-props object for both presentations (docked panel / drawer) so
  // they can never drift.
  const editChatProps = {
    liveWebSocket,
    reportId: reportID ?? "",
    reportType,
    narrativeOnly,
    handleSaveChanges: handleSaveCurrentChanges,
    fetchReportData: fetchReportDataWithSnapshot,
    showError: toast.error,
    onSendMessageRefReady: (fn: (message: string) => Promise<void>) => {
      chatSendMessageRef.current = fn;
    },
  };

  // The provider value: a per-render snapshot of the page's composition —
  // EditSectionPage stays the only owner of the hooks behind it.
  // Deliberately NOT memoized: the handlers (handleSubmit/handleUpdate/…)
  // close over per-render data, so a memo would hand consumers stale
  // closures. Context churn equals today's prop churn — the consumer
  // re-renders per keystroke via its zustand subscription anyway.
  const editSectionValue: EditSectionContextValue = {
    report: {
      reportId: reportID ?? "",
      reportType,
      rms: reportRms,
      status: reportStatus,
      allowEditing: isSessionReady,
      isReportOwner,
      showRMSCommit,
      narrativeOnly,
    },
    schema: {
      jsonSchema: jsonSchema ?? null,
      schemaMapping:
        schemaMappingMap[reportRms as keyof typeof schemaMappingMap],
    },
    video: { data: videoData, loadStatus: videoLoadStatus },
    save: {
      isSaving,
      updateFormData: handleUpdate,
      saveChanges: handleSaveCurrentChanges,
      submit: handleSubmit,
      generateFullReport: handleGenerateFullReport,
      isGeneratingFull,
      generationStartedAt,
      generationTimeoutMs,
    },
    session: { liveWebSocket, sessionSocket, logUserActivity: handleUserAction },
    agent: {
      refreshReportData: fetchReportDataWithSnapshot,
      formDataSnapshotRef,
    },
    chat: {
      sendMessageRef: chatSendMessageRef,
      setShowChat: setSidePanelOpen,
      available: aiAssistantEnabled,
    },
    userProfile,
  };

  return (
    <>
      <WarningDisplay warnings={warnings} onDismiss={() => setWarnings([])} />
      <InactivityModal
        open={showTimeoutWarning}
        onContinue={handleContinueSession}
        onLogout={handleTimeoutSave}
        expiryTimestamp={inactivityExpiryTimestamp}
      />

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          height: "calc(100vh - 96px)", // Full height minus app bar and padding
          overflow: "hidden", // Prevent outer scrolling
        }}
      >
        {/* Content Area + Sidebar Container */}
        <Box sx={{ display: "flex", flexGrow: 1, overflow: "hidden" }}>
          {/* Main Content Area */}
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden", // Allow children to scroll
              p: 3, // Inner padding for content
            }}
          >
            {coApprovalEnabled && coComments.length > 0 && (
              <Box sx={{ mb: 2, flexShrink: 0 }}>
                <CoReviewBanner
                  comments={coComments}
                  returnedAt={coReturnedAt}
                  onViewHistory={() => setHistoryOpen(true)}
                />
              </Box>
            )}

            {/* The AI Assistant is gated off for the rejected-report flow —
                say so instead of silently hiding it. */}
            {enableEditing &&
              reportStatus === ReportStatus.CHANGES_REQUESTED && (
                <Alert
                  severity="info"
                  icon={<AutoAwesomeIcon fontSize="small" />}
                  sx={{ mb: 2, flexShrink: 0 }}
                >
                  <AlertTitle>AI Assistant unavailable</AlertTitle>
                  The AI Assistant is turned off while a report is being
                  revised after Commanding Officer review. Address the
                  requested changes manually and resubmit for review.
                </Alert>
              )}

            {canCoFinalize && (
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                sx={{ mb: 2, flexShrink: 0 }}
              >
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<VerifiedOutlinedIcon />}
                  onClick={() => setSignatureDialogOpen(true)}
                  loading={isApproving}
                >
                  Approve report
                </Button>
                <Button
                  variant="outlined"
                  color="info"
                  startIcon={<UndoOutlinedIcon />}
                  onClick={() => setRetractConfirmOpen(true)}
                  loading={isRetracting}
                >
                  Retract rejection
                </Button>
              </Stack>
            )}

            <Box
              sx={{
                flexGrow: 1,
                minHeight: 0,
                overflow: "auto",
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
              }}
            >
              {coComments.length > 0 && (
                <Box
                  sx={{
                    p: 2,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    bgcolor: (t) =>
                      t.palette.mode === "dark"
                        ? "rgba(209, 154, 102, 0.04)"
                        : "rgba(255, 167, 38, 0.04)",
                  }}
                >
                  {generalComments.length > 0 && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.5,
                        mb:
                          narrativeComments.length > 0 ||
                          commentsBySection.length > 0
                            ? 1.5
                            : 0,
                        border: `1px solid ${theme.palette.warning.main}`,
                        borderRadius: 1,
                      }}
                    >
                      <Typography
                        variant="overline"
                        sx={{
                          fontWeight: 700,
                          color: "warning.dark",
                          display: "block",
                          mb: 1,
                        }}
                      >
                        General comments
                      </Typography>
                      <SectionCommentRail
                        sectionPath="$"
                        comments={generalComments}
                        mode={canResolveComments ? "officer" : "officer-readonly"}
                        reportOwnerUserId={reportOwnerUserId ?? undefined}
                        onResolve={handleResolveComment}
                        onUnresolve={handleUnresolveComment}
                        // TODO: re-enable when Fix with AI is implemented.
                        // onFixWithAi={handleFixCommentWithAi}
                        onReply={handleReplyComment}
                      />
                    </Paper>
                  )}

                  {narrativeTexts.length > 0 && narrativeComments.length > 0 && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.5,
                        mb: commentsBySection.length > 0 ? 1.5 : 0,
                        border: `1px solid ${theme.palette.warning.main}`,
                        borderRadius: 1,
                      }}
                    >
                      <Typography
                        variant="overline"
                        sx={{
                          fontWeight: 700,
                          color: "warning.dark",
                          display: "block",
                          mb: 1,
                        }}
                      >
                        Narrative inline comments
                      </Typography>
                      <Stack spacing={2}>
                        {narrativeTexts.map((n) => {
                          const items = narrativeCommentsByIndex.get(n.index);
                          if (!items || items.length === 0) return null;
                          return (
                            <Box key={n.index} data-co-narrative-index={n.index}>
                              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                Narrative {n.index + 1}
                              </Typography>
                              <NarrativeWithInlineComments
                                narrativeIndex={n.index}
                                text={n.text}
                                comments={items}
                                mode={canResolveComments ? "officer" : "co-readonly"}
                                reportOwnerUserId={reportOwnerUserId ?? undefined}
                                onResolveComment={handleResolveComment}
                                onUnresolveComment={handleUnresolveComment}
                                // TODO: re-enable when Fix with AI is implemented.
                                // onFixWithAiComment={handleFixCommentWithAi}
                                onReplyComment={handleReplyComment}
                              />
                            </Box>
                          );
                        })}
                      </Stack>
                    </Paper>
                  )}

                  {commentsBySection.length > 0 && (
                    <Box
                      sx={{
                        border: `1px solid ${theme.palette.warning.main}`,
                        borderRadius: 1,
                        p: 1.5,
                      }}
                    >
                      <Typography
                        variant="overline"
                        sx={{
                          fontWeight: 700,
                          color: "warning.dark",
                          display: "block",
                          mb: 1,
                        }}
                      >
                        Section comments
                      </Typography>
                      {commentsBySection.map(([section, items]) => (
                        <SectionCommentRail
                          key={section}
                          sectionPath={section}
                          fieldLabel={prettifySectionPath(section)}
                          comments={items}
                          mode={canResolveComments ? "officer" : "officer-readonly"}
                          reportOwnerUserId={reportOwnerUserId ?? undefined}
                          onResolve={handleResolveComment}
                          onUnresolve={handleUnresolveComment}
                          // TODO: re-enable when Fix with AI is implemented.
                          // onFixWithAi={handleFixCommentWithAi}
                          onReply={handleReplyComment}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              )}
              {/* Full mode needs the schema before EditExtractedInformation
                  can render — it casts jsonSchema to a non-null JSONSchema7.
                  `loading` is set by an effect that lags this render, so on
                  the narrative-only→full flip jsonSchema is briefly null while
                  loading is still false. Gate on the schema directly to avoid
                  rendering the full editor with a null schema. Editable
                  children also wait for the lock so their socket listeners
                  attach to an initialized session. */}
              {loading || !formData || (!narrativeOnly && !jsonSchema) ||
                (enableEditing && !isSessionReady && !isGeneratingFull) ? (
                <EditExtractedInformationSkeleton />
              ) : (
                <EditSectionProvider value={editSectionValue}>
                  {narrativeOnly ? (
                    <NarrativeOnlyEditor />
                  ) : (
                    <EditExtractedInformation />
                  )}
                </EditSectionProvider>
              )}
            </Box>

            {/* Hidden while the full report generates — saving/undo/exit are
                all unavailable until the locked run completes. */}
            {!isGeneratingFull && (
              <Box sx={{ mt: 2 }}>
                <EditSectionFooter
                  enableEditing={isSessionReady}
                  lastModifiedTime={lastModifiedTime}
                  isSaving={isSaving}
                  canUndo={canUndo}
                  canRedo={canRedo}
                  onUndo={handleUndo}
                  onRedo={handleRedo}
                  onInstantSave={handleInstantSave}
                  onSaveAndExit={handleSaveAndExit}
                />
              </Box>
            )}
          </Box>

          {/* Right Chat Sidebar (lg+: docked; below lg: floating button +
              temporary drawer — previously the chat simply did not exist
              below 1200px). The AI Assistant stays hidden in
              CHANGES_REQUESTED (a visible notice explains why) until the AI
              flow handles CO feedback context. */}
          {isLgUp && reportID && aiAssistantEnabled && (
            <ChatPanelContainer mode="chat" chatProps={editChatProps} />
          )}
        </Box>
      </Box>

      {!isLgUp && reportID && aiAssistantEnabled && (
        <>
          {!sidePanelOpen && (
            <Button
              variant="contained"
              startIcon={<AutoAwesomeIcon />}
              onClick={() => setSidePanelOpen(true)}
              sx={{
                position: "fixed",
                bottom: 24,
                // Clear of the global Help & Feedback Fab (fixed right 15,
                // width 56, zIndex 1001 — this pill's speedDial z would
                // otherwise bury it) and of its slide-up panel.
                right: 88,
                zIndex: theme.zIndex.speedDial,
                borderRadius: 999,
                boxShadow: 6,
              }}
            >
              AI Assistant
            </Button>
          )}
          <Drawer
            anchor="right"
            open={sidePanelOpen}
            onClose={() => setSidePanelOpen(false)}
            PaperProps={{ sx: { width: { xs: "100%", sm: 420 } } }}
          >
            <ChatPanel
              {...editChatProps}
              onClose={() => setSidePanelOpen(false)}
            />
          </Drawer>
        </>
      )}
      {reportID && coApprovalEnabled && (
        <ReviewHistoryDialog
          open={historyOpen}
          reportId={reportID}
          onClose={() => setHistoryOpen(false)}
        />
      )}

      <SignatureDialog
        open={signatureDialogOpen}
        mode="coApprove"
        signerName={officerUser.displayName}
        onClose={() => setSignatureDialogOpen(false)}
        onConfirm={handleApprove}
        loading={isApproving}
      />

      <RetractConfirmationDialog
        open={retractConfirmOpen}
        onClose={() => setRetractConfirmOpen(false)}
        onConfirm={handleRetract}
        loading={isRetracting}
      />
    </>
  );
};

export default EditSectionPage;
